import * as React from 'react';
import { Grid } from '@material-ui/core';
import { Refresh } from '@material-ui/icons';
import { DateTime } from 'luxon';
import MUIDataTable, { MUIDataTableColumnDef, MUIDataTableOptions } from 'mui-datatables';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { debounce } from 'throttle-debounce';
import { Header } from '../Header/Header';
import { ActionsMenu } from '../ActionsMenu/ActionsMenu';
import { ActionItem } from '../ActionsMenu/ActionItem';
import tracker from '../Loader/loader-tracker';
import { apiFetch } from '../../utilities/apiFetch';
import { MonitoringDateNavigator, parseMonitoringDateRange, parseMonitoringTba } from './MonitoringDateNavigator';
import './styles.css';

interface IOwnProps {
  apiPath: string;
  allowTba?: boolean;
  columns: MUIDataTableColumnDef[];
  emptyMessage: string;
  title: string;
}

type Props = IOwnProps & RouteComponentProps;

interface IState {
  endDate: DateTime;
  rows: object[];
  startDate: DateTime;
  tba: boolean;
}

export const buildMonitoringUrl = (
  apiPath: string,
  startDate: DateTime,
  endDate: DateTime,
  tba = false,
): string => {
  if (tba) return `${apiPath}/tba`;

  const params = new URLSearchParams({
    startDate: startDate.toISODate(),
    endDate: endDate.toISODate(),
  });
  return `${apiPath}?${params.toString()}`;
};

export const monitoringDateRangesMatch = (
  currentStartDate: DateTime,
  currentEndDate: DateTime,
  nextStartDate: DateTime,
  nextEndDate: DateTime,
): boolean =>
  currentStartDate.toISODate() === nextStartDate.toISODate()
  && currentEndDate.toISODate() === nextEndDate.toISODate();

export const formatMonitoringDeliveryDate = (value?: string | null): string => {
  if (!value) return 'TBA';

  const date = DateTime.fromISO(value);
  return date.isValid ? date.toFormat('dd/MM/yyyy') : value;
};

export class MonitoringPageComponent extends React.Component<Props, IState> {
  private readonly fetchRowsDebounced: () => void;

  constructor(props: Props) {
    super(props);
    const range = parseMonitoringDateRange(props.location.search);
    this.state = {
      endDate: range.endDate,
      rows: [],
      startDate: range.startDate,
      tba: !!props.allowTba && parseMonitoringTba(props.location.search),
    };
    this.fetchRowsDebounced = debounce(250, false, this.fetchRows);
  }

  public componentDidMount() {
    this.updateQueryString();
    this.fetchRows();
  }

  private fetchRows = async () => {
    const fetchPromise = (async () => {
      const response = await apiFetch(
        buildMonitoringUrl(this.props.apiPath, this.state.startDate, this.state.endDate, this.state.tba),
      );
      const rows = await response?.json();
      this.setState({ rows: Array.isArray(rows) ? rows : [] });
    })();

    await tracker.track(fetchPromise);
  };

  private handleDateRangeChange = (startDate: DateTime, endDate: DateTime) => {
    if (monitoringDateRangesMatch(this.state.startDate, this.state.endDate, startDate, endDate)) return;

    this.setState({ startDate, endDate }, () => {
      this.updateQueryString();
      this.fetchRowsDebounced();
    });
  };

  private handleTbaChange = (tba: boolean) => {
    this.setState({ tba }, () => {
      this.updateQueryString();
      this.fetchRowsDebounced();
    });
  };

  private updateQueryString = () => {
    const params = new URLSearchParams({
      startDate: this.state.startDate.toISODate(),
      endDate: this.state.endDate.toISODate(),
    });
    if (this.state.tba) params.set('tba', 'true');
    this.props.history.replace({
      search: params.toString(),
    });
  };

  public render() {
    const { columns, emptyMessage, title } = this.props;
    const { endDate, rows, startDate, tba } = this.state;
    const options: MUIDataTableOptions = {
      elevation: 0,
      rowsPerPage: 25,
      rowsPerPageOptions: [10, 25, 50, 100],
      selectableRows: false,
      print: false,
      download: true,
      downloadOptions: {
        filename: `${this.props.apiPath.split('/').pop()}-${DateTime.local().toFormat('yyyy-MM-dd-HHmm')}.csv`,
        separator: ',',
      },
      viewColumns: false,
      textLabels: {
        body: {
          noMatch: emptyMessage,
          toolTip: 'Sort',
        },
        pagination: {
          next: 'Next Page',
          previous: 'Previous Page',
          rowsPerPage: 'Rows per page:',
          displayRows: 'of',
        },
        toolbar: {
          search: 'Search',
          downloadCsv: 'Download CSV',
          print: 'Print',
          viewColumns: 'View Columns',
          filterTable: 'Filter Table',
        },
        filter: {
          all: 'All',
          title: 'FILTERS',
          reset: 'RESET',
        },
        viewColumns: {
          title: 'Show Columns',
          titleAria: 'Show/Hide Table Columns',
        },
        selectedRows: {
          text: 'row(s) selected',
          delete: 'Delete',
          deleteAria: 'Delete Selected Rows',
        },
      },
    };

    return (
      <React.Fragment>
        <Header title={title} />
        <section>
          <Grid container className="content monitoring">
            <Grid item xs={1} />
            <Grid item xs={10}>
              <MonitoringDateNavigator
                endDate={endDate}
                showTba={this.props.allowTba}
                startDate={startDate}
                tba={tba}
                onChange={this.handleDateRangeChange}
                onTbaChange={this.handleTbaChange}
              />
              <MUIDataTable title={title} data={rows} columns={columns} options={options} />
            </Grid>
            <Grid item xs={1} />
          </Grid>
        </section>
        <ActionsMenu>
          <ActionItem label="Refresh" onClick={this.fetchRows}>
            <Refresh />
          </ActionItem>
        </ActionsMenu>
      </React.Fragment>
    );
  }
}

export const MonitoringPage = withRouter(MonitoringPageComponent);
